// pro100доска: thin client for the "Мои доски" panel talking to the
// math-from-zero backend (a separate repo/service — see the rebrand plan's
// "Мои доски" section for the full architecture). The teacher token here is
// SEPARATE from the one the standalone teacher.html dashboard stores in its
// own localStorage — that page lives on a different origin, so its token
// can't be read from here even if a teacher is already logged in there.

const API_BASE = import.meta.env.VITE_APP_MY_BOARDS_API_URL as
  | string
  | undefined;

const TOKEN_KEY = "pro100doska-teacher-token";
const TOKEN_EXPIRY_KEY = "pro100doska-teacher-token-expiry";
// matches the backend's own TOKEN_EXPIRE_HOURS (app/core/security.py) — this
// is just for deciding when to silently drop a stale token and re-show the
// password gate, the backend still independently rejects an expired JWT.
const TOKEN_TTL_MS = 24 * 60 * 60 * 1000;

export class MyBoardsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export interface Student {
  id: number;
  name: string;
  emoji: string;
  display_name: string;
  is_active: boolean;
}

const apiUrl = (path: string) => {
  if (!API_BASE) {
    throw new Error("VITE_APP_MY_BOARDS_API_URL is not configured");
  }
  return `${API_BASE}${path}`;
};

const parseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    if (typeof body?.detail === "string") {
      return body.detail;
    }
    if (Array.isArray(body?.detail) && body.detail[0]?.msg) {
      return body.detail[0].msg;
    }
  } catch {
    // response wasn't JSON — fall through to a generic message below
  }
  return response.status === 401
    ? "Неверный пароль"
    : `Ошибка сервера (${response.status})`;
};

export const getStoredToken = (): string | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = Number(localStorage.getItem(TOKEN_EXPIRY_KEY));
  if (!token || !expiry || Date.now() > expiry) {
    return null;
  }
  return token;
};

export const clearStoredToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

const storeToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + TOKEN_TTL_MS));
};

export const teacherLogin = async (password: string): Promise<string> => {
  const response = await fetch(apiUrl("/auth/teacher"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    throw new MyBoardsApiError(
      await parseErrorMessage(response),
      response.status,
    );
  }

  const { token } = await response.json();
  storeToken(token);
  return token;
};

const authedFetch = async (path: string, token: string): Promise<Response> => {
  const response = await fetch(apiUrl(path), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) {
    clearStoredToken();
    throw new MyBoardsApiError("Сессия истекла, войдите снова", 401);
  }
  if (!response.ok) {
    throw new MyBoardsApiError(
      await parseErrorMessage(response),
      response.status,
    );
  }
  return response;
};

export const fetchStudents = async (token: string): Promise<Student[]> => {
  const response = await authedFetch("/students", token);
  return response.json();
};

export const fetchBoardLink = async (
  token: string,
  studentId: number,
): Promise<string> => {
  const response = await authedFetch(`/board/link/${studentId}`, token);
  const { url } = await response.json();
  return url;
};
